import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { num } from '../../format';
import {
  dismissCluster,
  listDismissals,
  listDuplicateClusters,
  mergeInto,
  undismissCluster,
  type Cluster,
  type MergeReport,
} from './api';

interface Props {
  onOpen: (componentId: string) => void;
}

/**
 * Live entries sharing a parametric signature, for unhurried cleanup.
 *
 * The signature was built to find candidates, never to forbid them, so a
 * cluster is a question rather than a verdict: two entries can legitimately
 * look identical and be different parts. Nothing here merges on its own — the
 * person picks which entry survives, and that choice matters because the other
 * one's stock is folded into it.
 */
export function DuplicateClusters({ onOpen }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<MergeReport | null>(null);

  const [showHidden, setShowHidden] = useState(false);

  const clusters = useQuery({
    queryKey: ['components', 'clusters'],
    queryFn: listDuplicateClusters,
  });
  const hidden = useQuery({
    queryKey: ['components', 'clusters', 'dismissed'],
    queryFn: listDismissals,
  });

  const dismiss = useMutation({
    mutationFn: (cluster: Cluster) =>
      dismissCluster(
        cluster.identity_key,
        cluster.components.map((item) => item.id),
      ),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['components', 'clusters'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const restore = useMutation({
    mutationFn: (id: string) => undismissCluster(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['components', 'clusters'] });
    },
  });

  const merge = useMutation({
    mutationFn: ({ source, target }: { source: string; target: string }) =>
      mergeInto(source, target),
    onSuccess: (report) => {
      setError(null);
      setDone(report);
      void queryClient.invalidateQueries({ queryKey: ['components'] });
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  if (clusters.isLoading) return <p className="panel__hint">{t('common.loading')}</p>;

  const rows = clusters.data ?? [];

  return (
    <section className="clusters">
      <h2>{t('components.clusters.title')}</h2>
      <p className="panel__hint">{t('components.clusters.intro')}</p>

      {done && (
        <p className="clusters__done">
          {t('components.clusters.merged', { code: done.source_code })}
        </p>
      )}
      {error && (
        <p className="panel__error">
          {t(`components.errors.${error}`, t('components.errors.unknown'))}
        </p>
      )}

      {rows.length === 0 && <p className="panel__hint">{t('components.clusters.empty')}</p>}

      {rows.map((cluster) => (
        <ClusterCard
          key={cluster.identity_key}
          cluster={cluster}
          busy={merge.isPending || dismiss.isPending}
          onOpen={onOpen}
          onMerge={(source, target) => merge.mutate({ source, target })}
          onDismiss={() => dismiss.mutate(cluster)}
        />
      ))}

      {/* A decision made in haste has to be reachable again, and once a cluster
          is set aside it is gone from the list above by design. */}
      {(hidden.data?.length ?? 0) > 0 && (
        <div className="clusters__hidden">
          <button
            type="button"
            className="component-form__scope"
            onClick={() => setShowHidden(!showHidden)}
          >
            {showHidden
              ? t('components.clusters.hideDismissed')
              : t('components.clusters.showDismissed', { n: hidden.data?.length ?? 0 })}
          </button>
          {showHidden && (
            <ul className="clusters__members">
              {(hidden.data ?? []).map((row) => (
                <li key={row.id}>
                  <span className="components__code">{row.identity_key.slice(0, 12)}…</span>
                  <span>{t('components.clusters.dismissedEntries', {
                    n: row.component_ids.length,
                  })}</span>
                  <span className="stock__when">
                    {new Date(row.created_at).toLocaleDateString()}
                  </span>
                  <button type="button" onClick={() => restore.mutate(row.id)}>
                    {t('components.clusters.restore')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function ClusterCard({
  cluster,
  busy,
  onOpen,
  onMerge,
  onDismiss,
}: {
  cluster: Cluster;
  busy: boolean;
  onOpen: (componentId: string) => void;
  onMerge: (sourceId: string, targetId: string) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  // Defaults to the first entry, but the choice is always visible: whichever is
  // kept absorbs the others' stock, and that is not a detail to bury.
  const [keepId, setKeepId] = useState(cluster.components[0]?.id ?? '');

  return (
    <article className="clusters__card">
      <ul className="clusters__members">
        {cluster.components.map((item) => (
          <li key={item.id}>
            <label>
              <input
                type="radio"
                name={`keep-${cluster.identity_key}`}
                checked={keepId === item.id}
                onChange={() => setKeepId(item.id)}
              />
              <button type="button" className="duplicates__open" onClick={() => onOpen(item.id)}>
                {item.name}
              </button>
              <span className="components__code">{item.internal_code}</span>
              {item.mpn && <em>{item.mpn}</em>}
              <span className="duplicates__stock">
                {t('components.duplicates.onHand', { n: num(item.qty_on_hand) })}
              </span>
            </label>
          </li>
        ))}
      </ul>

      <div className="clusters__actions">
        {cluster.components
          .filter((item) => item.id !== keepId)
          .map((item) => (
            <button
              key={item.id}
              type="button"
              disabled={busy || !keepId}
              onClick={() => onMerge(item.id, keepId)}
            >
              {t('components.clusters.mergeInto', { code: item.internal_code })}
            </button>
          ))}

        {/* Three boards can share a signature and be three boards. Saying so
            once has to be enough, or the screen becomes noise and takes the
            real duplicates down with it. */}
        <button type="button" disabled={busy} onClick={onDismiss}>
          {t('components.clusters.dismiss')}
        </button>
      </div>

      <p className="clusters__note">{t('components.clusters.labelsKeepWorking')}</p>
    </article>
  );
}
